import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Container, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import { AgentInboxesContextProvider } from "./AgentInboxesContext";
import AgentInboxesGrid from "./AgentInboxesGrid";
import CreateAgentInboxDialog from "./CreateAgentInboxDialog";

const AgentInboxesSettings = () => {
  const { hasPermissions } = useClientContext();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const getInboxes = useCallback(() => adminApi.searchInboxes({}), []);

  const [inboxes, fetchInboxesError] = useFetch(getInboxes);

  const [agents, fetchAgentConfigurationsError] = useFetch(adminApi.getAgents);

  const fetchError = fetchInboxesError || fetchAgentConfigurationsError;
  if (fetchError) {
    logError(fetchError, "[AgentInboxesSettings] fetch");
    return <DataLoadingFailed />;
  }

  const hasEditPermissions = hasPermissions(["ManageInboxes"]);

  const filteredInboxes = searchValue
    ? (inboxes ?? []).filter((inbox) => inbox.name.toLowerCase().includes(searchValue.toLowerCase()))
    : (inboxes ?? []);

  return (
    <AgentInboxesContextProvider hasEditPermissions={hasEditPermissions} agents={agents ?? []}>
      <Container maxWidth="xl" sx={{ height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <RecordCounter records={inboxes?.length ?? 0} hide={inboxes === undefined} />
          {hasEditPermissions && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <SearchField debounceTimeMs={300} onSearch={setSearchValue} disabled={!inboxes?.length} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateDialog(true)}>
                Add New
              </Button>
            </Stack>
          )}
        </Box>

        <AgentInboxesGrid rows={filteredInboxes} loading={inboxes === undefined || agents === undefined} />
      </Container>
      {showCreateDialog && <CreateAgentInboxDialog onClose={() => setShowCreateDialog(false)} />}
    </AgentInboxesContextProvider>
  );
};

export default AgentInboxesSettings;
